<template>
    <TransitionRoot as="template" :show="show">
        <Dialog as="div" class="fixed inset-0 overflow-hidden" @close="close()">
            <div class="absolute inset-0 overflow-hidden">
                <DialogOverlay class="absolute inset-0 bg-gray-500 opacity-75"/>

                <div class="fixed inset-y-0 pl-16 max-w-full right-0 flex">
                    <TransitionChild as="template" enter="transform transition ease-in-out duration-500 sm:duration-700"
                                     enter-from="translate-x-full" enter-to="translate-x-0"
                                     leave="transform transition ease-in-out duration-500 sm:duration-700"
                                     leave-from="translate-x-0" leave-to="translate-x-full">
                        <div class="w-screen max-w-lg">
                            <form class="h-full divide-y divide-gray-200 flex flex-col bg-white shadow-xl"
                                  @submit.prevent="submit">
                                <div class="flex-1 h-0 overflow-y-auto">
                                    <div class="py-6 px-4 bg-dark-pitch-purple sm:px-6">
                                        <div class="flex items-center justify-between">
                                            <DialogTitle class="text-lg text-white" v-if="form.id">
                                                Update Folder `{{ form.name }}`
                                            </DialogTitle>
                                            <DialogTitle class="text-lg text-white" v-else>
                                                Create Folder
                                            </DialogTitle>
                                            <div class="ml-3 h-7 flex items-center">
                                                <button type="button"
                                                        class="bg-dark-pitch-purple rounded-md text-indigo-200 hover:text-white focus:outline-none focus:ring-2 focus:ring-white"
                                                        @click="close()">
                                                    <span class="sr-only">Close panel</span>
                                                    <XIcon class="h-6 w-6" aria-hidden="true"/>
                                                </button>
                                            </div>
                                        </div>
                                        <div class="mt-1">
                                            <p class="text-sm text-indigo-300">
                                                Get started by filling in the information below to create a new
                                                folder.
                                            </p>
                                        </div>
                                    </div>
                                    <SlideOutErrors :show="showErrors === true"/>
                                    <div class="flex-1 flex flex-col justify-between">
                                        <div class="px-4 divide-y divide-gray-200 sm:px-6">
                                            <div class="space-y-6 pt-6 pb-5">
                                                <div>
                                                    <label for="folder-name"
                                                           class="block text-sm text-gray-900 required">
                                                        Name
                                                    </label>
                                                    <div class="mt-1">
                                                        <input type="text" id="folder-name" v-model="form.name"
                                                               class="block w-full shadow-sm sm:text-sm focus:ring-indigo-500 focus:border-light-pitch-purple border-gray-300 rounded-md"
                                                               :class="{'border-red-500': form.errors.name}"
                                                        />
                                                    </div>
                                                </div>
                                                <div class="col-span-4 clear-both">
                                                    <label
                                                        class="block text-sm font-medium text-gray-700">Description</label>
                                                    <div class="mt-1">
                                                        <quill-editor content-type="html" v-model:content="form.description"
                                                                      :class="{'border-red-500': form.errors.description}"
                                                                      class="min-h-[150px] mt-1 block w-full bg-white border border-gray-300 shadow-sm py-2 px-3"
                                                                      theme="snow"></quill-editor>
                                                    </div>
                                                </div>

                                            </div>
                                            <div class="pt-4 pb-6">
                                                <div class="mt-4 flex text-sm">
                                                    <a href="#"
                                                       class="group inline-flex items-center text-gray-500 hover:text-gray-900">
                                                        <ExclamationCircleIcon
                                                            class="h-5 w-5 text-gray-400 group-hover:text-gray-500"
                                                            aria-hidden="true"/>
                                                        <span class="ml-2">
                                                            To add a new folder to an existing folder, right click the folder within the quote and then click 'Add New Folder'.
                                                        </span>
                                                    </a>
                                                </div>
                                            </div>
                                        </div>
                                    </div>

                                </div>
                                <div class="flex-shrink-0 px-4 py-4 flex justify-end">
                                    <button type="button"
                                            class="bg-white py-2 px-4 border border-gray-300 rounded-md shadow-sm text-sm text-gray-700 hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500"
                                            @click="close()">
                                        Close
                                    </button>
                                    <button :disabled="form.processing || !form.isDirty" type="submit"
                                            class=" ml-4 inline-flex justify-center py-2 px-4 border border-transparent shadow-sm text-sm rounded-md text-white bg-pitch-purple hover:bg-dark-pitch-purple focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500 disabled:bg-gray-200">
                                        <span v-if="updating === true">Update</span>
                                        <span v-else>Save</span>
                                    </button>
                                </div>
                            </form>
                        </div>
                    </TransitionChild>
                </div>
            </div>
        </Dialog>
    </TransitionRoot>
</template>
<script>
import {ref, reactive} from 'vue'
import {Dialog, DialogOverlay, DialogTitle, TransitionChild, TransitionRoot} from '@headlessui/vue'
import {XIcon} from '@heroicons/vue/outline'
import {LinkIcon, PlusSmIcon, QuestionMarkCircleIcon, ExclamationCircleIcon} from '@heroicons/vue/solid'
import {useForm} from '@inertiajs/vue3'
import { router } from '@inertiajs/vue3'
import SlideOutErrors from "@/Components/SlideOutErrors";
import {emitter} from "@/eventHub";


export default {
    components: {
        SlideOutErrors,
        Dialog,
        DialogOverlay,
        DialogTitle,
        TransitionChild,
        TransitionRoot,
        LinkIcon,
        PlusSmIcon,
        QuestionMarkCircleIcon,
        ExclamationCircleIcon,
        XIcon,
    },
    props: {
      fixture : Object,
      quote: Object
    },
    data() {
        return {
            show: ref(false),
            showErrors: ref(false),
            updating: false,
            form: useForm({
                id: null,
                name: null,
                quote_id: null,
                parent_id: null,
                description: ""
            })
        }
    },
  mounted() {

    // Open the folder slideout for folder creation.
    emitter.on("createFolderSlideout", (params) => {
      this.updating = false;
      this.form.quote_id = this.quote.id
      this.form.parent_id = typeof params.folder_id !== "undefined" ? params.folder_id : null;
      this.open();
    });

    // Open the folder slideout for editing a folder.
    emitter.on("openFolderSlideout", (folder) => {
      this.updating = true;
      this.form.id = folder.id
      this.form.name = folder.name
      this.form.quote_id = this.quote.id;
      this.form.description = folder.description;
      this.open();
    });

  },
  methods: {
        submit() {
            if (this.updating) {
                this.form.post(route('frontend.fixture.quote.folder.update', [this.fixture.id, this.quote.id, this.form.id]), {
                    onSuccess: page => {
                        this.show = false;
                        this.showErrors = false;
                        this.form.reset();
                    },
                    onError: errors => {
                        this.showErrors = true;
                    },
                    preserveScroll: true
                });
            } else {
                this.form.post(route('frontend.fixture.quote.folder.create', [this.fixture.id, this.quote.id]), {
                    onSuccess: page => {
                        this.show = false;
                        this.showErrors = false;
                        this.form.reset();
                    },
                    onError: errors => {
                        this.showErrors = true;
                    },
                    preserveScroll: true
                });
            }
        },

        close() {
            this.form.reset();
            this.form.clearErrors();
            this.show = false;
        },

        open() {
            this.form.clearErrors();
            this.show = true;
        }
    }
}
</script>
